import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Pixel tolerance applied to scroll-edge checks so sub-pixel rounding never leaves a stale chevron. */
const SCROLL_EDGE_TOLERANCE_PX = 4;

/** Pixel distance scrolled per click of the left/right scroll affordance. */
const SCROLL_STEP_PX = 120;

// ─── Types ────────────────────────────────────────────────────────────────────

export type LifecycleStepState = 'active' | 'done' | 'upcoming';

export interface LifecycleConnector {
  /** Number of collapsed steps represented by this connector */
  collapsedCount?: number;
  /** Visual style: solid for done, dashed border for upcoming */
  kind: 'done' | 'upcoming';
  /** Pre-resolved tooltip label (e.g. "8 done", "2 upcoming"). Falls back to "${collapsedCount} ${kind}" when absent. */
  label?: string;
}

export interface LifecycleStep {
  /** Lucide icon for done/active connector leading into this step (optional) */
  connector?: LifecycleConnector;
  /** Label shown below the step marker */
  label: React.ReactNode;
  /** Optional reason shown below label (e.g. failure reason) */
  reason?: React.ReactNode;
  /** Visual state of this step */
  state: LifecycleStepState;
  /** Timestamp shown above the step label */
  timestamp?: React.ReactNode;
}

export interface LifecycleTimelineProps {
  /** Optional additional CSS classes */
  className?: string;
  /** Accessible label for the scroll-left button (pass an i18n string) */
  scrollLeftLabel?: string;
  /** Accessible label for the scroll-right button (pass an i18n string) */
  scrollRightLabel?: string;
  /** The ordered steps to display */
  steps: LifecycleStep[];
}

// ─── Subcomponents ────────────────────────────────────────────────────────────

interface StepMarkerProps {
  readonly state: LifecycleStepState;
}

function StepMarker({ state }: StepMarkerProps): React.JSX.Element {
  return (
    <div
      className={cn('w-2 h-2 rounded-full shrink-0', {
        'bg-muted border border-border': state === 'upcoming',
        'bg-primary': state === 'done' || state === 'active',
        'ring-2 ring-primary/20': state === 'active',
      })}
    />
  );
}

interface ConnectorProps {
  readonly connector: LifecycleConnector;
}

function ConnectorBar({ connector }: ConnectorProps): React.JSX.Element {
  const isDone = connector.kind === 'done';
  const hasCollapsed = connector.collapsedCount !== undefined && connector.collapsedCount > 0;

  if (hasCollapsed) {
    const label = connector.label ?? `${connector.collapsedCount} ${connector.kind}`;

    return (
      <div className="shrink-0 w-4 mx-0.5 mt-2 flex items-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="relative flex-1 group cursor-help">
                <div
                  className={cn('border-t-2 border-dashed', {
                    'border-border': !isDone,
                    'border-primary/60': isDone,
                  })}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <span className="text-xs">{label}</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div className="shrink-0 w-4 mx-0.5 mt-2 flex items-center">
      <div
        className={cn('flex-1 h-px', {
          'bg-border': !isDone,
          'bg-primary/60': isDone,
        })}
      />
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Horizontal 5-slot lifecycle stepper.
 *
 * - Horizontally scrollable with edge-fade + chevron affordances when overflowing.
 * - Auto-centers the active step on mount.
 * - Collapsed connectors show a dashed line with a hover tooltip (e.g. "8 done").
 */
export function LifecycleTimeline({
  className,
  scrollLeftLabel = 'Scroll left',
  scrollRightLabel = 'Scroll right',
  steps,
}: LifecycleTimelineProps): React.JSX.Element {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  // Find the active step index for auto-centering
  const activeIndex = steps.findIndex((s) => s.state === 'active');

  function checkScroll(): void {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > SCROLL_EDGE_TOLERANCE_PX);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - SCROLL_EDGE_TOLERANCE_PX);
  }

  // Mount-only: register the resize listener once and clean it up on unmount.
  // Separated from the steps effect so the listener is not re-registered on every render.
  React.useEffect(() => {
    function handleResize(): void {
      checkScroll();
    }

    window.addEventListener('resize', handleResize);
    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Re-check scroll availability whenever steps change (including on mount) so that
  // overflow chevrons/fades are visible immediately — not only after the first scroll event.
  React.useEffect(() => {
    checkScroll();
  }, [steps]);

  // Auto-center active step on mount; cancel the RAF on cleanup
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || activeIndex < 0) return undefined;

    const rafId = requestAnimationFrame(() => {
      const stepEls = el.querySelectorAll<HTMLElement>('[data-step]');
      const activeEl = stepEls[activeIndex];
      if (activeEl) {
        const offsetLeft = activeEl.offsetLeft;
        const halfStep = activeEl.offsetWidth / 2;
        const halfContainer = el.clientWidth / 2;
        el.scrollLeft = offsetLeft + halfStep - halfContainer;
      }
      checkScroll();
    });

    return (): void => {
      cancelAnimationFrame(rafId);
    };
  }, [activeIndex]);

  function scrollBy(direction: 'left' | 'right'): void {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ behavior: 'smooth', left: direction === 'left' ? -SCROLL_STEP_PX : SCROLL_STEP_PX });
  }

  return (
    <div className={cn('rounded-lg border bg-card p-4 shadow-sm', className)}>
      <div className="relative">
        {/* Left scroll affordance */}
        {canScrollLeft && (
          <button
            aria-label={scrollLeftLabel}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-card/90 border border-border shadow-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => {
              scrollBy('left');
            }}
            type="button"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Right scroll affordance */}
        {canScrollRight && (
          <button
            aria-label={scrollRightLabel}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-card/90 border border-border shadow-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => {
              scrollBy('right');
            }}
            type="button"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Fade overlay — left */}
        {canScrollLeft && (
          <div className="pointer-events-none absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-card to-transparent z-[5]" />
        )}

        {/* Fade overlay — right */}
        {canScrollRight && (
          <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-card to-transparent z-[5]" />
        )}

        {/* Scrollable row */}
        <div
          className="flex items-start w-full overflow-x-auto scrollbar-hide pb-1"
          onScroll={checkScroll}
          ref={scrollRef}
        >
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              {/* Connector before this step (skip for the first) */}
              {index > 0 && step.connector !== undefined && <ConnectorBar connector={step.connector} />}
              {index > 0 && step.connector === undefined && (
                <div className="shrink-0 w-4 mx-0.5 mt-2 flex items-center">
                  <div
                    className={cn('flex-1 h-px', {
                      'bg-border': step.state === 'upcoming',
                      'bg-primary/60': step.state !== 'upcoming',
                    })}
                  />
                </div>
              )}

              {/* Step slot */}
              <div
                className="flex flex-col items-center gap-1 min-w-0 flex-1 basis-0"
                data-step={index}
                data-state={step.state}
              >
                <StepMarker state={step.state} />

                {step.timestamp !== undefined ? (
                  <span
                    className={cn('text-xs leading-tight text-center break-words', {
                      'italic text-muted-foreground': step.state === 'upcoming',
                      'text-muted-foreground': step.state !== 'upcoming',
                    })}
                  >
                    {step.timestamp}
                  </span>
                ) : (
                  <span className="text-xs leading-tight text-center break-words invisible select-none">-</span>
                )}

                <span
                  className={cn('text-xs leading-tight text-center break-words', {
                    'font-bold text-foreground': step.state === 'active',
                    'text-foreground': step.state === 'done',
                    'text-muted-foreground': step.state === 'upcoming',
                  })}
                >
                  {step.label}
                </span>

                {step.reason !== undefined && (
                  <span className="text-xs leading-tight text-center break-words text-destructive italic">
                    {step.reason}
                  </span>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
